import {
  LOGIN,
  LOGOUT,
  SIGNUP,
  RELOAD_USER,
  RELOAD_AUTH,
  REFRESH_TOKEN,
  CONNECT_FACEBOOK,
  FETCH_GEO_IP,
  LOGIN_WITH_FACEBOOK,
  GET_FACEBOOK_STATUS,
  UPDATE_API_CONFIGS,
  INIT_ONBOARDING,
  INIT_FACEBOOK_ONBOARDING,
  INIT_SIGNUP_WITH_FACEBOOK,
  SIGNUP_WITH_FACEBOOK,
  LOGIN_OR_SIGNUP_WITH_FACEBOOK,
  REQUEST_PASSWORD_RESET,
  CLEAR_LEGACY,
  GET_FB_PROFILE,
  STASH_EMAIL_FOR_ONBOARDING,
  REFRESH_AND_RELOAD,
  STAGE_USER,
  FB_ONBOARDING,
  UACF_FB_ONBOARDING,
  CANCEL_ONBOARDING_PROMPT,
  FIND_USER_BY_EMAIL,
} from './constants'

import {
  selectFbConnected,
  selectFbAccessToken,
  selectFbUserId,
  selectFbExpiry,
  selectUacfId,
  selectSignupEmail,
} from 'services/auth/selectors'

export function fetchGeoIp () {
  return (dispatch, getState, { auth }) => {
    return dispatch({
      type: FETCH_GEO_IP,
      payload: auth.getGeoIp(),
    })
  }
}

export function updateApiConfigs (authToken) {
  return (dispatch, getState, { mapmy, premium }) => {

    // reload api instances with the new token
    mapmy.updateConfig({ authToken })
    premium.updateConfig({ authToken })

    return dispatch({
      type: UPDATE_API_CONFIGS,
      payload: { mapmy, premium },
    })
  }
}

export function reloadUser () {
  return (dispatch, getState, { mapmy }) => {
    const promise = Promise.all([
      mapmy.getSelf(),
      mapmy.getPremiumStatus('me'),
    ]).catch((err) => {
      if (err && err.response && err.response.status && err.response.status >= 500) {
        err.user = true
        return Promise.reject(err)
      }
    })

    return dispatch({
      type: RELOAD_USER,
      payload: promise,
    })
  }
}

export function reloadAuth (authToken) {
  return (dispatch) => {
    const promise = Promise.resolve()
      .then(() => {
        return dispatch(updateApiConfigs(authToken))
      })
      .then(() => {
        return dispatch(reloadUser())
      })
      .then(({ value: [ user, premiumStatus ] }) => {
        return { user, premiumStatus, authToken }
      })

    return dispatch({
      type: RELOAD_AUTH,
      payload: promise,
    })
  }
}

export function initOnboarding (options) {
  return {
    type: INIT_ONBOARDING,
    payload: options,
  }
}

export function initFacebookOnboarding (options) {
  return {
    type: INIT_FACEBOOK_ONBOARDING,
    payload: options,
  }
}

export function login ({ username, password }, reload = false) {
  return (dispatch, getState, { auth }) => {
    const promise = Promise.resolve()
      .then(() => auth.postLogin({ username, password }))
      .then((response) => {
        if (response.data && response.data.item && response.data.item.onboarding) {
          return dispatch(initOnboarding({
            uacfId: response.data.item.uacfId,
            uacfProfile: response.data.item.profile,
            username: username,
            password: password,
          }))
        } else {
          if (reload) {
            const authToken = response.data.item.authToken
            return dispatch(reloadAuth(authToken))
          }
        }
      })

    return dispatch({
      type: LOGIN,
      payload: promise,
    })
  }
}

export function logout () {
  return (dispatch, getState, { auth }) => {
    return dispatch({
      type: LOGOUT,
      payload: auth.getLogout(),
    })
  }
}

export function refreshToken () {
  return (dispatch, getState, { auth }) => {
    return dispatch({
      type: REFRESH_TOKEN,
      payload: auth.refreshToken(),
    })
  }
}

export function findUserByEmail (email) {
  return (dispatch, getState, { auth }) => {
    return dispatch({
      type: FIND_USER_BY_EMAIL,
      payload: auth.findUserByEmail(email),
    })
  }
}

export function signup (values, reload = false) {
  return (dispatch, getState, { auth }) => {
    const state = getState()
    const data = { ...values }
    const uacfId = selectUacfId(state)

    if (uacfId) { data.uacfId = uacfId }

    const promise = Promise.resolve()
      .then(() => auth.postSignup(data))
      .then((response) => {
        if (reload) {
          const authToken = response.data.item.access_token
          return dispatch(reloadAuth(authToken))
        }
      })

    return dispatch({
      type: SIGNUP,
      payload: promise,
    })
  }
}

export function signupWithFacebook (values) {
  return (dispatch, getState, { auth }) => {
    const state = getState()
    const uacfId = selectUacfId(state)
    const data = {
      ...values,
      facebookToken: selectFbAccessToken(state),
      facebookId: selectFbUserId(state),
      facebookExpiry: selectFbExpiry(state),
    }

    if (uacfId) { data.uacfId = uacfId }

    const promise = Promise.resolve()
      .then(() => auth.postSignupWithFacebook(data))
      .then((response) => {
        const authToken = response.data.item.access_token
        return dispatch(reloadAuth(authToken))
      })

    return dispatch({
      type: SIGNUP_WITH_FACEBOOK,
      payload: promise,
    })
  }
}

export function connectFacebook () {
  return (dispatch, getState, { fb }) => {
    return dispatch({
      type: CONNECT_FACEBOOK,
      payload: fb.login(),
    })
  }
}

export function getFbProfile () {
  return (dispatch, getState, { fb }) => {
    const token = selectFbAccessToken(getState())
    return dispatch({
      type: GET_FB_PROFILE,
      payload: fb.getSelf(token),
    })
  }
}

export function initSignupWithFacebook () {
  return (dispatch, getState) => {
    const promise = Promise.resolve()
      .then(() => {
        // connect w/ facebook, if not already connected
        const connected = selectFbConnected(getState())
        if (!connected) { return dispatch(connectFacebook()) }
      })
      .then(() => {
        return dispatch(getFbProfile())
      })

    return dispatch({
      type: INIT_SIGNUP_WITH_FACEBOOK,
      payload: promise,
    })
  }
}

export function loginWithFacebook (reload = false) {
  return (dispatch, getState, { auth }) => {
    const promise = Promise.resolve()
      .then(() => {
        // connect w/ facebook, if not already connected
        const connected = selectFbConnected(getState())
        if (!connected) { return dispatch(connectFacebook()) }
      })
      .then(() => {
        // once we're connected, exchange FB token for auth-token
        const state = getState()
        const token = selectFbAccessToken(state)
        const userId = selectFbUserId(state)
        return auth.postFacebookToken({ token, userId })
      })
      .then((response) => {
        if (response.data && response.data.item && response.data.item.onboarding) {
          return dispatch(getFbProfile())
            .then((fbProfile) => {
              return dispatch(initFacebookOnboarding({
                fbProfile: fbProfile,
                uacfId: response.data.item.uacfId,
                uacfProfile: response.data.item.profile,
              }))
            })
        } else {
          if (reload) {
            const authToken = response.data.item.authToken
            return dispatch(reloadAuth(authToken))
          }
        }
      })

    return dispatch({
      type: LOGIN_WITH_FACEBOOK,
      payload: promise,
    })
  }
}

export function getFacebookLoginStatus () {
  return (dispatch, getState, { fb }) => {
    return dispatch({
      type: GET_FACEBOOK_STATUS,
      payload: fb.getLoginStatus(),
    })
  }
}

export function loginOrSignupWithFacebook () {
  return (dispatch) => {
    const promise = Promise.resolve()
      .then(() => dispatch(loginWithFacebook()))
      .catch(() => dispatch(initSignupWithFacebook()))

    return dispatch({
      type: LOGIN_OR_SIGNUP_WITH_FACEBOOK,
      payload: promise,
    })
  }
}

export function requestPasswordReset (email, redirectURI) {
  return (dispatch, getState, { auth }) => {
    return dispatch({
      type: REQUEST_PASSWORD_RESET,
      payload: auth.requestPasswordReset(email, redirectURI),
    })
  }
}

export function clearLegacy () {
  return (dispatch, getState, { auth }) => {
    return dispatch({
      type: CLEAR_LEGACY,
      payload: auth.getClearLegacy(),
    })
  }
}

export function stashEmailForOnboarding (formName) {
  return (dispatch, getState) => {
    const email = selectSignupEmail(formName)(getState())
    return dispatch({
      type: STASH_EMAIL_FOR_ONBOARDING,
      payload: email,
    })
  }
}

export function stageUser (values) {
  return (dispatch) => {
    return dispatch({
      type: STAGE_USER,
      payload: values,
    })
  }
}

export function refreshAndReload ({ quiet = true } = {}) {
  return (dispatch) => {
    const promise = Promise.resolve()
      .then(() => dispatch(refreshToken()))
      .then((response) => {
        const authToken = response.value.data.item.authToken
        return dispatch(reloadAuth(authToken))
      })

    return dispatch({
      type: REFRESH_AND_RELOAD,
      payload: promise,
      meta: { quiet },
    })
  }
}

export function signupUser (onboarding, values, reload) {
  return (dispatch) => {
    const createUser = onboarding === FB_ONBOARDING || onboarding === UACF_FB_ONBOARDING
      ? signupWithFacebook
      : signup
    return dispatch(createUser(values, reload))
  }
}

export function cancelOnboardingPrompt () {
  return (dispatch) => {
    return dispatch({
      type: CANCEL_ONBOARDING_PROMPT,
    })
  }
}



// WEBPACK FOOTER //
// ./src/services/auth/actions.js